/** App shell: navigation, tenant switcher, language switcher. */

import { NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { SUPPORTED_LANGUAGES, setLanguage, type Language } from '../i18n';
import { useAuth } from '../lib/auth';

export function Layout() {
  const { t, i18n } = useTranslation();
  const { me, logout, selectTenant, can } = useAuth();

  return (
    <div className="shell">
      <header className="shell__header">
        <div className="brand">
          <img
            className="brand__logo"
            src="/logo-usolar.png"
            srcSet="/logo-usolar.png 1x, /logo-usolar@2x.png 2x"
            alt={t('app.name')}
            width={244}
            height={56}
          />
          <span className="muted">{t('app.tagline')}</span>
        </div>

        <nav className="shell__nav" aria-label={t('app.name')}>
          <NavLink to="/" end>
            {t('nav.portfolio')}
          </NavLink>
          {can('reports.read') ? <NavLink to="/mis">{t('nav.mis')}</NavLink> : null}
          <NavLink to="/compare">{t('nav.compare')}</NavLink>
          <NavLink to="/incidents">{t('nav.incidents')}</NavLink>
          {can('admin.sources.read') ? <NavLink to="/connectors">{t('nav.admin')}</NavLink> : null}
        </nav>

        <div className="shell__tools">
          {me && me.tenants.length > 1 ? (
            <label className="field field--inline">
              <span className="sr-only">{t('app.tenant')}</span>
              <select
                value={me.active_tenant_id ?? ''}
                onChange={(event) => void selectTenant(event.target.value)}
              >
                {me.tenants.map((tenant) => (
                  <option key={tenant.tenant_id} value={tenant.tenant_id}>
                    {tenant.name}
                  </option>
                ))}
              </select>
            </label>
          ) : me?.tenants[0] ? (
            <span className="tenant-name">{me.tenants[0].name}</span>
          ) : null}

          <label className="field field--inline">
            <span className="sr-only">{t('app.language')}</span>
            <select
              value={i18n.language.slice(0, 2)}
              onChange={(event) => setLanguage(event.target.value as Language)}
            >
              {SUPPORTED_LANGUAGES.map((language) => (
                <option key={language} value={language}>
                  {language.toUpperCase()}
                </option>
              ))}
            </select>
          </label>

          <button type="button" className="button button--ghost" onClick={() => void logout()}>
            {t('app.logout')}
          </button>
        </div>
      </header>

      <main className="shell__main">
        <Outlet />
      </main>

      <footer className="shell__footer">
        <span className="muted">
          {me?.email} · {me?.role}
        </span>
      </footer>
    </div>
  );
}
